/**
 * Generator 4: Column definitions — (authenticated)/<n>/columns.tsx
 * Generates TanStack Table column definitions for entity list views
 */

import { flattenFields } from '@core-engine/schema/field-helpers/index.js'
import type { GeneratedFile, GeneratorOptions, Schema } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { pluralize, singularize, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import {
  generateDeleteDialogFileTemplate,
  generateRowActionsFileTemplate
} from '@next-adapter/generators/columns/column-actions.js'
import {
  generateColumnDef,
  generateFirstColumnDef,
  isSortableColumn
} from '@next-adapter/generators/columns/column-defs.js'
import { createCustomCellComponent } from '@next-adapter/generators/columns/custom-cell.js'

// ============================================================================
// Public API
// ============================================================================

export interface ColumnsGeneratorResult {
  files: GeneratedFile[]
}

/**
 * Generate column definitions for entity list view
 */
export function generateColumns(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): ColumnsGeneratorResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const columns = schema.columns ?? []
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const Actions = `${Singular}Actions`
  const DeleteDialog = `Delete${Singular}Dialog`
  const deleteDialogFileName = `delete-${singular}-dialog`

  const flatFields = flattenFields(schema.fields || [])

  // --- Determine needed imports ---
  let needsSortIcons = false
  let needsBadge = false
  let needsButton = false
  let needsImageRenderer = false
  let needsTruncate = false

  for (const column of columns) {
    if (isSortableColumn(column)) {
      needsSortIcons = true
      needsButton = true
    }
    if (column.type === 'badge' || column.type === 'boolean') needsBadge = true
    if (column.type === 'image' || column.type === 'avatar') needsImageRenderer = true
    if (column.type === 'link') needsTruncate = true
  }

  const hasDelete = schema.actions?.delete ?? false

  // --- Build lucide icons ---
  const lucideIcons: string[] = []

  // --- Build UI component imports (individual per-component) ---
  const uiImports: string[] = []
  if (needsButton) uiImports.push("import { Button } from '@admin/components/ui/button'")
  if (needsBadge) uiImports.push("import { Badge } from '@admin/components/ui/badge'")
  if (hasDelete) uiImports.push("import { Checkbox } from '@admin/components/ui/checkbox'")
  if (needsImageRenderer) {
    uiImports.push("import { Avatar, AvatarFallback } from '@admin/components/ui/avatar'")
  }

  // --- Custom component imports ---
  const customImports: string[] = []
  const customFiles: GeneratedFile[] = []
  for (const column of columns) {
    if (column.type === 'custom' && column.component) {
      customImports.push(`import { ${column.component} } from './cells/${column.component}'`)
      customFiles.push(...createCustomCellComponent(schema, column.component, pagesDir, options))
    }
  }

  const utilityImports: string[] = []
  if (needsSortIcons) {
    utilityImports.push("import { SortIndicator } from '@admin/components/shared/sort-indicator'")
  }
  if (needsTruncate) utilityImports.push("import { truncateText } from '@admin/utils/text/text'")

  // --- Generate columns ---
  const firstColumn = columns[0]
  const restColumns = columns.slice(1)

  const firstColDef = generateFirstColumnDef(firstColumn, flatFields, { selectable: hasDelete })
  const restColDefs = restColumns.map((col) => generateColumnDef(col, flatFields)).join(',\n')

  // --- Actions column ---
  const actionsColumn = hasDelete
    ? `  {
    id: 'actions',
    size: 64,
    enableResizing: false,
    meta: { width: '64px' },
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row }) => (
      <div className="flex justify-end">
        <${Actions} id={row.original.id} />
      </div>
    )
  }`
    : ''

  // --- Assemble file ---
  const content = `'use client'

import type { ColumnDef } from '@tanstack/react-table'
${lucideIcons.length > 0 ? `import { ${lucideIcons.join(', ')} } from 'lucide-react'` : ''}
${needsImageRenderer ? "import Image from 'next/image'" : ''}
${uiImports.join('\n')}
${utilityImports.join('\n')}
import type { ${Singular} } from '@admin/actions/${actionImportPath}'
${customImports.join('\n')}
${hasDelete ? `import { ${Actions} } from './actions'\n` : ''}
export const columns: ColumnDef<${Singular}>[] = [
${firstColDef}${restColDefs ? `,\n${restColDefs}` : ''}${actionsColumn ? `,\n${actionsColumn}` : ''}
]
`
  const files = [createGeneratedFile(`${pagesDir}/${schema.name}/columns.tsx`, content)]

  if (hasDelete) {
    files.push(
      createGeneratedFile(
        `${pagesDir}/${schema.name}/actions.tsx`,
        generateRowActionsFileTemplate({
          Actions,
          DeleteDialog,
          deleteDialogFileName,
          singular
        })
      ),
      createGeneratedFile(
        `${pagesDir}/${schema.name}/${deleteDialogFileName}.tsx`,
        generateDeleteDialogFileTemplate({
          Singular,
          actionImportPath,
          singular,
          plural
        })
      )
    )
  }

  return {
    files: [...files, ...customFiles]
  }
}
